import React, { useMemo, useRef } from 'react';
import { useCompareKeyMetricsChart } from '../hooks';
import { dealWithMentionsSentimentChart, getGameColorOrName } from '../utils';
import { echartsContent, sortMapAuxiliaryFunc } from '@/pages/opinion/utils';
import { formatTimeXAxisText, getXAxisAxisLabel } from '@op/utils/chart';
import useLanguage from '@op/hooks/useLanguage';
import { formatChartParamsDate } from '@op/components/ChartCommon/formatter';
import EChartCommon, { getAxisPointer } from '@op/components/EChartCommon';
import TableCommonHook from '@op/components/TableCommonHook';
import { SortBy } from 'tea-component/lib/table/addons';
import './index.scss';
import { scoreFormat } from '@op/utils/value';
import { formatDateFunc } from '@op/utils/time';
import TableColumn from '../component/TableColumn';
import CardCommon from '@op/components/CardCommon';
import { getGameDataIndex } from '@op/components/TableCommonHook/hooks/useCompareKeyMetricsColumns';
import { TableHeight } from '../constants';
import ProgressColumn from '../component/ProgressColumn';
import { TableColumn as TableColumnType } from 'tea-component/es/table/TableProps';
export default function GoogleTrendsCharts({
  filter,
  chartType,
  gameData,
  setSortBy,
  sortBy,
  sortColumns,
  tableColumns,
}: {
  filter: any;
  chartType: string;
  gameData: {
    [id: string]: {
      name: string;
      color: string;
    };
  }[];
  setSortBy?: React.Dispatch<React.SetStateAction<SortBy<any>>>;
  sortBy?: SortBy<any>;
  sortColumns?: string[];
  tableColumns?: TableColumnType<any>[];
}) {
  const { formatMessage } = useLanguage();

  const params = useMemo(
    () => ({
      stats_types: ['google_trends'],
      tab_type: 'google_trends',
      ...filter,
    }),
    [filter],
  );
  const { data: statistics, loading } = useCompareKeyMetricsChart({
    filter: params,
    dealWithToChart: dealWithMentionsSentimentChart,
  });

  const getGameData = getGameColorOrName(gameData);

  const xAxisIntervalLimit = 8;
  const chartRef = useRef(null);
  const { xAxisData, yAxisData, tableData } = statistics;
  const hasStatistics = Array.isArray(xAxisData) && xAxisData.length > 0;

  const columns = useMemo(
    () => [
      {
        header: formatMessage({ id: 'Date' }),
        key: 'time',
        align: 'left',
        render: (cvm: any) => formatDateFunc(params?.date_type)(cvm.time),
      },
      {
        header: formatMessage({ id: 'Game' }),
        key: 'name',
        align: 'left',
        render: (cvm: any) => {
          const item: any = getGameData(cvm?.name, 'all');
          return <TableColumn name={item?.entity_name} item={item} />;
        },
      },
      {
        header: formatMessage({ id: 'Google Trends' }),
        key: 'value',
        align: 'center',
        render: (cvm: any) => (
          <ProgressColumn
            percentage={cvm.ratio}
            emptyValue={'-'}
            value={cvm.value}
            formateValue={(value) => scoreFormat(value, 0)}
            oldTime={[]}
            newTime={[]}
            oldValue={''}
          ></ProgressColumn>
        ),
      },
    ],
    [formatMessage, params?.date_type],
  );

  const options = useMemo(() => {
    if (hasStatistics) {
      let legendData: {
        name: string;
        itemStyle: {
          color: string;
        };
      }[] = [];
      let seriesData: {
        itemStyle?: any;
        barMaxWidth?: string;
        barGap?: string;
        label?: any;
        name: string;
        type: string;
        stack?: string;
        data: (number | null)[];
        symbolSize?: number;
      }[] = [];
      yAxisData.map((item) => {
        const color = getGameData(item.name, 'color');
        seriesData.push({
          name: item.name,
          type: chartType,
          stack: chartType === 'bar' ? 'total' : '',
          data:
            item?.data?.map((item: number) => {
              // 断点处理
              if (item === 0) return null;
              return item;
            }) || [],
          barMaxWidth: '70px',
          itemStyle: { color },
          symbolSize: 7,
        });
        legendData.push({
          name: item.name,
          itemStyle: { color },
        });
      });
      const sortMap = getGameDataIndex(gameData);
      legendData.sort(sortMapAuxiliaryFunc(sortMap, (item) => item.name));
      const option = {
        tooltip: {
          trigger: 'axis',
          borderWidth: 0,
          padding: 0,
          confine: true,
          extraCssText: 'z-index:8',
          formatter: (data: any[]) => {
            const sumKey = data[0].axisValue;

            let timeText = '';
            if (params?.date_type) {
              timeText = formatDateFunc(params?.date_type)(sumKey);
            }

            return formatChartParamsDate({
              params: data,
              title: timeText,
              rightTitle: formatMessage({ id: 'Google Trends' }),
              listTitleFunc: (item) => getGameData(item.seriesName),
              valueFormat: (value) => scoreFormat(value, 0),
              isSort: true,
            });
          },
        },
        legend: {
          data: legendData,
          formatter: (params: string) => getGameData(params),
        },
        axisPointer: getAxisPointer((data) =>
          formatTimeXAxisText(data.value, params?.date_type),
        ),
        xAxis: {
          type: 'category',
          boundaryGap: ['20%', 0],
          data: xAxisData,
          axisLabel: {
            ...getXAxisAxisLabel(filter),
            interval: xAxisData?.length > xAxisIntervalLimit ? 'auto' : 0,
            fontSize: 10,
            showMaxLabel: true,
          },
        },
        yAxis: {
          type: 'value',
          axisLabel: {
            formatter: (value: any) => echartsContent(value),
            fontSize: 11,
          },
        },
        series: seriesData,
      };
      return option;
    }
    return {};
  }, [
    hasStatistics,
    yAxisData,
    xAxisData,
    chartType,
    getGameData,
    params?.date_type,
    formatMessage,
  ]);

  return (
    <div ref={chartRef} className="dbo-pr-trend-chart">
      <CardCommon
        contentCard
        loading={loading}
        loadingHeight={TableHeight.CHART_WITHOUT_SUBTITLE}
        empty={!hasStatistics}
        hasEmptyImg={false}
        hasEmptyTitle={false}
        emptyHeight={TableHeight.CHART_WITHOUT_SUBTITLE}
        emptyDescText={
          `Currently, only global data comparisons are supported, if you need Google Trends Data for other countries/regions, please contact {contact}.`
        }
      >
        {chartType !== 'table' ? (
          <EChartCommon
            options={options}
            height={TableHeight.CHART_WITHOUT_SUBTITLE}
          />
        ) : (
          <TableCommonHook
            recordKey={'comment_uin'}
            records={tableData || []}
            total={tableData?.length || 0}
            columns={tableColumns || columns}
            sortColumns={sortColumns || ['time', 'value']}
            hasPageable={false}
            defaultSort={sortBy}
            bordered="all"
            changeSort={setSortBy}
            sortType={'local'}
            minWidth={'auto'}
            maxHeight={TableHeight.CHART_TABLE}
          />
        )}
      </CardCommon>
    </div>
  );
}
